import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  interval,
  catchError,
  filter,
  take,
  tap,
  throwError,
  timeout, of
} from 'rxjs';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import {TranslateService} from "@ngx-translate/core";

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private baseUrl = environment.apiUrl;
  private appUrl = environment.appUrl;
  private refreshTokenInProgress = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  private userInfoSubject = new BehaviorSubject<any>(null);
  public userInfo$ = this.userInfoSubject.asObservable();

  // Neue Properties für den automatischen Refresh
  private tokenExpiration: number = 0;
  private refreshTokenTimerSubscription: Subscription | null = null;

  constructor(private http: HttpClient, private _router: Router, private _translateService: TranslateService) {}

  register(user: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/register/`, user, { withCredentials: true });
  }

  login(phone_number: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/login/`, { phone_number });
  }

  verifyCode(phone_number: string, code: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/verify/`, { phone_number, code });
  }

  loginWithPassword(credentials: { email: string; password: string }): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/login-password/`, credentials);
  }


  saveToken(token: any): void {
    localStorage.setItem('access_token', token.access);
    localStorage.setItem('refresh_token', token.refresh);
    this.tokenExpiration = this.getTokenExpirationFromToken(token.access);
    this.startRefreshTokenTimer();
  }

  getAccessToken(): string | null {
    return localStorage.getItem('access_token');
  }

  getRefreshToken(): string | null {
    return localStorage.getItem('refresh_token');
  }

  refreshToken(): Observable<any> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      // Falls kein Refresh-Token vorhanden ist, Logout auslösen und ein Fehler zurückgeben.
      this.clearTokens();
      return throwError(() => new Error('No refresh token available'));
    }

    console.log('Starting refresh token process.');
    if (this.refreshTokenInProgress) {
      console.log('refresh token in process.');
      return this.refreshTokenSubject.pipe(
        filter(result => result !== null),
        take(1)
      );
    } else {
      console.log('refresh token not in process.');
      this.refreshTokenInProgress = true;
      this.refreshTokenSubject.next(null);

      console.log('before post request');
      return this.http.post(`${this.baseUrl}/auth/api/token/refresh/`, { refresh: refreshToken })
        .pipe(
          timeout(3000), // 5 Sekunden Timeout
          tap((tokens: any) => {
            console.log('Received tokens:', tokens);
            this.saveToken(tokens);
            this.refreshTokenInProgress = false;
            this.refreshTokenSubject.next(tokens);
          }),
          catchError((err) => {
            this.refreshTokenInProgress = false;
            console.error('Error refreshing token', err);
            /*
            const currentLang = this._translateService.currentLang || 'en';
            this._router.navigate([`/${currentLang}`]).then(() => {
              // Optional: Seite neu laden, um alle Zustände zu resetten
              window.location.reload();
            });

             */
            return throwError(() => err);
          })
        );
    }
  }


  clearTokens(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    this.stopRefreshTokenTimer();
  }

  logout(): void {
    this.clearTokens();
    const currentLang = this._translateService.currentLang || 'en';
    this._router.navigate([`/${currentLang}`]);
  }

  handleError(error: any): Observable<never> {
    if (error.status === 401) {
      this.logout();
    }
    return throwError(() => error);
  }

/*
  getUserInfo(): Observable<any> {
    return this.http.get(`${this.baseUrl}/auth/user/info/`);
  }*/


  loadUserInfo(): Observable<any> {
    return this.http.get(`${this.baseUrl}/auth/user/info/`).pipe(
      tap(info => this.userInfoSubject.next(info)),
      catchError(err => {
        this.userInfoSubject.next(null);
        throw err;
      })
    );
  }

  // Bestehende Methode zum Abrufen der User-Infos, wenn sie schon geladen sind:
  getUserInfo(): Observable<any> {
    if (this.userInfoSubject.value) {
      return of(this.userInfoSubject.value);
    } else {
      return this.loadUserInfo();
    }
  }

  isUserAdmin(): boolean {
    const info = this.userInfoSubject.value;
    return info && info.groups && info.groups.includes('admin');
  }


  // In DjangoApiService oder AuthService (je nachdem, wie du es strukturierst)
  getUserProfile(): Observable<any> {
    return this.http.get(`${this.baseUrl}/auth/profile/`);
  }

  updateUserProfile(profileData: any): Observable<any> {
    return this.http.put(`${this.baseUrl}/auth/profile/`, profileData);
  }

  forgotPassword(email: string): Observable<any> {
    const url = this.appUrl
    return this.http.post(`${this.baseUrl}/auth/password-reset/`, { email, url });
  }

  // In auth.service.ts
  resetPassword(token: any, uid: any, new_password: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/password-reset/confirm/`, {
      token,
      uid,
      new_password
    });
  }



  isTokenExpired(token: string): boolean {
    try {
      const { exp } = jwtDecode<{ exp: number }>(token);
      if (exp === undefined) return false;

      const date = new Date(0);
      date.setUTCSeconds(exp);
      return date.valueOf() < new Date().valueOf();
    } catch (error) {
      return true;
    }
  }

  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    return token !== null && !this.isTokenExpired(token);
  }



  private getTokenExpirationFromToken(token: string): number {
    try {
      const { exp } = jwtDecode<{ exp: number }>(token);
      return exp * 1000;
    } catch (error) {
      return 0;
    }
  }

  startRefreshTokenTimer(): void {
    this.stopRefreshTokenTimer();

    const now = Date.now();
    const timeout = this.tokenExpiration - now - 60 * 1000; // 1 Minute vor Ablauf erneuern
    if (timeout > 0) {
      this.refreshTokenTimerSubscription = interval(timeout).pipe(
        tap(() => {
          this.refreshToken().subscribe({
            next: (tokens: any) => {
              // Neuer Token wurde bereits gespeichert
            },
            error: (err) => {
              this.logout();
            }
          });
        })
      ).subscribe();
    }
  }

  stopRefreshTokenTimer(): void {
    if (this.refreshTokenTimerSubscription) {
      this.refreshTokenTimerSubscription.unsubscribe();
      this.refreshTokenTimerSubscription = null;
    }
  }
}
